import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ramadanService, RamadanExpose } from '../services/ramadanService';

type SectionKey = 'VCN' | 'HDR' | 'SANTE';
type SectionFilter = SectionKey | 'ALL';
const SECTION_ORDER: SectionKey[] = ['VCN', 'HDR', 'SANTE'];
const SECTION_LABELS: Record<SectionKey, string> = { VCN: 'VCN (Université)', HDR: 'HDR', SANTE: 'SANTÉ' };
const SECTION_ICONS: Record<SectionKey, string> = { VCN: '🎓', HDR: '🏛️', SANTE: '🩺' };

type MobileCard = { section: SectionKey; expose: RamadanExpose };

export const Ramadan: React.FC = () => {
  const [exposes, setExposes] = useState<RamadanExpose[]>([]);
  const [upcomingBySection, setUpcomingBySection] = useState<{ VCN: RamadanExpose[]; HDR: RamadanExpose[]; SANTE: RamadanExpose[] }>({ VCN: [], HDR: [], SANTE: [] });
  const [selectedSection, setSelectedSection] = useState<SectionFilter>('ALL');
  const [mobileIndex, setMobileIndex] = useState(0);
  const [desktopSectionIndices, setDesktopSectionIndices] = useState<Record<SectionKey, number>>({
    VCN: 0,
    HDR: 0,
    SANTE: 0,
  });
  const [activeDesktopSectionIndex, setActiveDesktopSectionIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'cards' | 'grid'>('cards');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState({ status: '', section: '', annee_ramadan: new Date().getFullYear() });

  useEffect(() => { fetchUpcomingExposes(); }, []);

  const mobileCards = useMemo<MobileCard[]>(() => {
    if (selectedSection === 'ALL') {
      return SECTION_ORDER.flatMap((section) => (upcomingBySection[section] || []).map((expose) => ({ section, expose })));
    }
    const section = selectedSection as SectionKey;
    return (upcomingBySection[section] || []).map((expose) => ({ section, expose }));
  }, [selectedSection, upcomingBySection]);

  useEffect(() => { setMobileIndex(0); }, [selectedSection, mobileCards.length]);
  useEffect(() => {
    if (mobileCards.length <= 1) return;
    const i = setInterval(() => setMobileIndex((prev) => (prev + 1) % mobileCards.length), 7000);
    return () => clearInterval(i);
  }, [mobileCards]);

  useEffect(() => {
    setDesktopSectionIndices((prev) => {
      const next = { ...prev };
      let changed = false;
      SECTION_ORDER.forEach((section) => {
        const listLength = upcomingBySection[section]?.length || 0;
        if (listLength === 0) {
          if (next[section] !== 0) {
            next[section] = 0;
            changed = true;
          }
        } else if ((prev[section] ?? 0) >= listLength) {
          next[section] = 0;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [upcomingBySection]);

  useEffect(() => {
    const interval = setInterval(() => {
      setDesktopSectionIndices((prev) => {
        const next = { ...prev };
        let changed = false;
        SECTION_ORDER.forEach((section) => {
          const list = upcomingBySection[section] || [];
          if (list.length > 3) {
            const nextIndex = ((prev[section] ?? 0) + 1) % list.length;
            if (nextIndex !== prev[section]) {
              next[section] = nextIndex;
              changed = true;
            }
          } else if ((prev[section] ?? 0) !== 0) {
            next[section] = 0;
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    }, 8000);
    return () => clearInterval(interval);
  }, [upcomingBySection]);

  const sectionsWithUpcoming = useMemo(
    () => SECTION_ORDER.filter((section) => (upcomingBySection[section]?.length || 0) > 0),
    [upcomingBySection],
  );

  useEffect(() => {
    if (sectionsWithUpcoming.length === 0) {
      setActiveDesktopSectionIndex(0);
      return;
    }
    setActiveDesktopSectionIndex((prev) => prev % sectionsWithUpcoming.length);
  }, [sectionsWithUpcoming]);

  useEffect(() => {
    if (selectedSection !== 'ALL') {
      setActiveDesktopSectionIndex(0);
      return;
    }
    if (sectionsWithUpcoming.length <= 1) {
      setActiveDesktopSectionIndex(0);
      return;
    }
    const interval = setInterval(() => {
      setActiveDesktopSectionIndex((prev) => (prev + 1) % sectionsWithUpcoming.length);
    }, 2000);
    return () => clearInterval(interval);
  }, [selectedSection, sectionsWithUpcoming]);

  const handleDesktopPrev = (section: SectionKey) => {
    const list = upcomingBySection[section] || [];
    if (list.length <= 3) {
      return;
    }
    setDesktopSectionIndices((prev) => {
      const current = prev[section] ?? 0;
      const nextIndex = (current - 1 + list.length) % list.length;
      return { ...prev, [section]: nextIndex };
    });
  };

  const handleDesktopNext = (section: SectionKey) => {
    const list = upcomingBySection[section] || [];
    if (list.length <= 3) {
      return;
    }
    setDesktopSectionIndices((prev) => {
      const current = prev[section] ?? 0;
      const nextIndex = (current + 1) % list.length;
      return { ...prev, [section]: nextIndex };
    });
  };

  const handleMobilePrev = () => {
    if (mobileCards.length <= 1) {
      return;
    }
    setMobileIndex((prev) => (prev - 1 + mobileCards.length) % mobileCards.length);
  };

  const handleMobileNext = () => {
    if (mobileCards.length <= 1) {
      return;
    }
    setMobileIndex((prev) => (prev + 1) % mobileCards.length);
  };

  useEffect(() => { setCurrentPage(1); }, [filters]);
  useEffect(() => { fetchExposes(); }, [filters, currentPage, upcomingBySection.VCN.length, upcomingBySection.HDR.length, upcomingBySection.SANTE.length]);

  const fetchUpcomingExposes = async () => {
    try {
      const [vcnData, hdrData, santeData] = await Promise.all([
        ramadanService.getAll({ section: 'VCN', ordering: '-date_expose' }).catch(() => ({ results: [] })),
        ramadanService.getAll({ section: 'HDR', ordering: '-date_expose' }).catch(() => ({ results: [] })),
        ramadanService.getAll({ section: 'SANTE', ordering: '-date_expose' }).catch(() => ({ results: [] })),
      ]);
      const filterUpcoming = (arr: any[]) => (arr || [])
        .filter((e: RamadanExpose) => e.status_auto === 'a_venir' || e.status_auto === 'en_cours')
        .sort((a: RamadanExpose, b: RamadanExpose) => {
          // Ramadan n'a pas d'heure_debut, seulement date_expose
          const dateA = new Date(a.date_expose);
          const dateB = new Date(b.date_expose);
          return dateA.getTime() - dateB.getTime();
        });
      const vcn = filterUpcoming(vcnData.results || vcnData);
      const hdr = filterUpcoming(hdrData.results || hdrData);
      const sante = filterUpcoming(santeData.results || santeData);
      setUpcomingBySection({ VCN: vcn, HDR: hdr, SANTE: sante });
      setDesktopSectionIndices({ VCN: 0, HDR: 0, SANTE: 0 });
    } catch (error) {
      console.error('Erreur:', error);
      setUpcomingBySection({ VCN: [], HDR: [], SANTE: [] });
    }
  };

  const fetchExposes = async () => {
    setLoading(true);
    try {
      const params: any = { page: currentPage, ordering: '-created_at', ...filters };
      if (filters.status && filters.status !== '') {
        delete params.status;
        params.status_auto = filters.status;
      }
      const data = await ramadanService.getAll(params);
      let all = data.results || data;
      const idsToExclude = [...upcomingBySection.VCN, ...upcomingBySection.HDR, ...upcomingBySection.SANTE].map(e => e.id);
      all = all.filter((e: RamadanExpose) => !idsToExclude.includes(e.id));
      setExposes(all);
      const totalWithPriority = data.count || (data.results || data).length;
      setTotalCount(totalWithPriority);
      setTotalPages(totalWithPriority ? Math.ceil(totalWithPriority / 6) : 1);
    } catch (error) {
      console.error('Erreur:', error);
      setExposes([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusLabel = (statusAuto?: string, status?: string) => {
    const currentStatus = statusAuto || status || 'a_venir';
    const labels: { [key: string]: { text: string; icon: string; color: string } } = {
      a_venir: { text: 'À venir', icon: '⏳', color: 'bg-blue-100 text-blue-800' },
      en_cours: { text: 'En cours', icon: '🟢', color: 'bg-green-100 text-green-800' },
      termine: { text: 'Terminé', icon: '✅', color: 'bg-gray-100 text-gray-800' },
      passe: { text: 'Passé', icon: '📜', color: 'bg-gray-100 text-gray-800' },
    };
    return labels[currentStatus] || labels['a_venir'];
  };

  const renderUpcomingCard = (section: SectionKey, expose?: RamadanExpose) => {
    if (!expose) {
      return null;
    }
    const statusLabel = getStatusLabel(expose.status_auto, expose.status);
    return (
      <Link to={`/ramadan/${expose.url_slug || expose.id}`} className="group block h-full">
        <div className="bg-white rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 overflow-hidden border-2 border-purple-100/80 h-full flex flex-col">
          <div className="relative h-48 bg-gradient-to-br from-green-600 via-green-700 to-green-800 overflow-hidden">
            {expose.photo_exposant ? (
              <img src={expose.photo_exposant} alt={expose.prenom_exposant} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center text-white">
                <span className="text-7xl mb-2 opacity-80">🌙</span>
                {expose.jour_ramadan && <span className="text-xl font-bold">Jour {expose.jour_ramadan}</span>}
              </div>
            )}
            {expose.jour_ramadan && (
              <div className="absolute top-4 right-4">
                <span className="px-4 py-2 rounded-full text-sm font-bold bg-white/20 backdrop-blur-md text-white border border-white/30">Jour {expose.jour_ramadan}</span>
              </div>
            )}
            <div className="absolute top-4 left-4">
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusLabel.color} shadow-sm`}>{statusLabel.icon} {statusLabel.text}</span>
            </div>
          </div>
          <div className="p-6 flex-1 flex flex-col space-y-4">
            <div className="flex items-center space-x-2 text-sm text-purple-700 font-semibold">
              <span>{SECTION_ICONS[section]}</span>
              <span>{SECTION_LABELS[section]}</span>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 group-hover:text-purple-700 transition-colors line-clamp-2">{expose.theme}</h3>
            <div className="space-y-2 text-sm text-gray-600">
              <p className="text-base font-medium text-gray-800">👤 {expose.prenom_exposant} {expose.nom_exposant}</p>
              <p>🎓 {expose.classe}</p>
              <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
                <span>📅 {new Date(expose.date_expose).toLocaleDateString('fr-FR')}</span>
                <span>📅 Année {expose.annee_ramadan}</span>
              </div>
            </div>
            <div className="mt-auto pt-4 border-t border-gray-100 flex items-center justify-between text-sm text-gray-500">
              <div className="flex items-center space-x-4">
                <span className="flex items-center space-x-1"><span>👁️</span><span>{expose.nombre_vues || 0}</span></span>
                <span className="flex items-center space-x-1"><span>❤️</span><span>{expose.nombre_likes || 0}</span></span>
              </div>
              <span className="text-purple-700 font-semibold inline-flex items-center space-x-1 group-hover:translate-x-1 transition-transform">
                <span>Voir détails</span>
                <span>→</span>
              </span>
            </div>
          </div>
        </div>
      </Link>
    );
  };

  const getDesktopUpcomingRamadanExposes = (section: SectionKey): RamadanExpose[] => {
    const list = upcomingBySection[section] || [];
    if (list.length === 0) {
      return [];
    }
    if (list.length <= 3) {
      return list;
    }
    const startIndex = desktopSectionIndices[section] ?? 0;
    return [0, 1, 2].map((offset) => list[(startIndex + offset) % list.length]);
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-white via-green-50/30 to-green-50/50 py-12 sm:py-16 md:py-20 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-20 -right-20 w-96 h-96 bg-green-200/20 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-20 -left-20 w-96 h-96 bg-green-300/20 rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-4 sm:mb-6">
            <div className="relative inline-block bg-gradient-to-br from-green-600 via-green-700 to-green-800 rounded-3xl px-8 py-8 shadow-2xl overflow-hidden group">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
              <div className="relative z-10">
                <h1 className="text-5xl sm:text-6xl font-bold text-white mb-4 drop-shadow-lg">Exposés Ramadan</h1>
                <p className="text-green-50 text-xl font-medium">Découvrez nos exposés spéciaux du mois sacré</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section Prioritaire - Exposés Ramadan à venir */}
      {(upcomingBySection.VCN.length + upcomingBySection.HDR.length + upcomingBySection.SANTE.length) > 0 && (
        <section className="relative bg-gradient-to-br from-amber-50 via-purple-50 to-orange-50 py-10 sm:py-14 md:py-16 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-0 right-0 w-96 h-96 bg-amber-200/20 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-80 h-80 bg-green-200/20 rounded-full blur-3xl"></div>
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center mb-6 sm:mb-10">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">Prochains Exposés Ramadan</h2>
              <p className="text-gray-600 text-base sm:text-lg">Par section (VCN, HDR, SANTÉ)</p>
            </div>

            {/* Boutons de filtrage horizontaux */}
            <div className="flex items-center justify-center gap-2 sm:gap-3 mb-8 flex-wrap">
              {(['ALL', 'VCN', 'HDR', 'SANTE'] as SectionFilter[]).map((key) => (
                <button key={key} onClick={() => setSelectedSection(key)} className={`px-3 sm:px-4 py-2 rounded-full text-sm sm:text-base font-semibold transition-all border-2 ${selectedSection === key ? 'bg-purple-600 text-white border-purple-700 shadow-md' : 'bg-white text-gray-700 border-purple-200 hover:bg-purple-50'}`}>
                  {key === 'ALL' ? 'Toutes' : SECTION_LABELS[key as SectionKey]}
                </button>
              ))}
            </div>

            {/* Mobile: 1 carte */}
            <div className="lg:hidden">
              {mobileCards.length > 0 ? (
                <div className="relative">
                  {mobileCards.map((card, idx) => (
                    <div key={`${card.section}-${card.expose.id}`} className={`transition-opacity duration-500 ${idx === mobileIndex ? 'opacity-100 block' : 'opacity-0 hidden'}`}>
                      {renderUpcomingCard(card.section, card.expose)}
                    </div>
                  ))}
                  {mobileCards.length > 1 && (
                    <>
                      <button
                        type="button"
                        onClick={handleMobilePrev}
                        className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 text-purple-700 shadow-lg rounded-full p-2 hover:bg-purple-50 transition"
                        aria-label="Exposé Ramadan précédent"
                      >
                        ←
                      </button>
                      <button
                        type="button"
                        onClick={handleMobileNext}
                        className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 text-purple-700 shadow-lg rounded-full p-2 hover:bg-purple-50 transition"
                        aria-label="Exposé Ramadan suivant"
                      >
                        →
                      </button>
                    </>
                  )}
                </div>
              ) : (
                <div className="bg-white/80 rounded-2xl p-8 text-center text-gray-600 border border-purple-100">Aucun exposé Ramadan à venir</div>
              )}
            </div>

            {/* Desktop: sections empilées */}
            <div className="hidden lg:flex flex-col gap-8">
              {(() => {
                const sectionsToDisplay: SectionKey[] =
                  selectedSection === 'ALL'
                    ? (sectionsWithUpcoming.length > 0
                      ? [sectionsWithUpcoming[activeDesktopSectionIndex % sectionsWithUpcoming.length]]
                      : [])
                    : [selectedSection as SectionKey];

                if (selectedSection === 'ALL' && sectionsWithUpcoming.length === 0) {
                  return (
                    <div className="h-full flex items-center justify-center rounded-3xl bg-white/70 border-2 border-dashed border-purple-200 p-8 text-center text-gray-500">
                      Aucun exposé Ramadan à venir
                    </div>
                  );
                }

                return sectionsToDisplay.map((section) => (
                  <div key={section} className="space-y-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xl">{SECTION_ICONS[section]}</span>
                        <h3 className="text-xl font-bold text-gray-900">{SECTION_LABELS[section]}</h3>
                      </div>
                      {(upcomingBySection[section]?.length || 0) > 3 && (
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => handleDesktopPrev(section)}
                            className="bg-white text-purple-700 border border-purple-200 rounded-full px-3 py-1 shadow hover:bg-purple-50 transition"
                            aria-label={`Exposés Ramadan précédents ${SECTION_LABELS[section]}`}
                          >
                            ←
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDesktopNext(section)}
                            className="bg-white text-purple-700 border border-purple-200 rounded-full px-3 py-1 shadow hover:bg-purple-50 transition"
                            aria-label={`Exposés Ramadan suivants ${SECTION_LABELS[section]}`}
                          >
                            →
                          </button>
                        </div>
                      )}
                    </div>
                    {(() => {
                      const desktopItems = getDesktopUpcomingRamadanExposes(section);
                      if (desktopItems.length === 0) {
                        return (
                          <div className="h-full flex items-center justify-center rounded-3xl bg-white/70 border-2 border-dashed border-purple-200 p-8 text-center text-gray-500">
                            Aucun exposé Ramadan à venir pour {SECTION_LABELS[section]}
                          </div>
                        );
                      }
                      // Toujours utiliser grid-cols-3, les cartes gardent leur taille normale
                      return (
                        <div className="grid grid-cols-3 gap-4">
                          {desktopItems.map((expose) => (
                            <div key={expose.id}>{renderUpcomingCard(section, expose)}</div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                ));
              })()}
            </div>
          </div>
        </section>
      )}

      {/* Filtres mobiles - après la section prioritaire */}
      <div className="lg:hidden max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-green-100/50">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="px-3 py-2 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white text-sm">
              <option value="">Tous les statuts</option>
              <option value="a_venir">⏳ À venir</option>
              <option value="en_cours">🟢 En cours</option>
              <option value="termine">✅ Terminé</option>
              <option value="passe">📜 Passé</option>
            </select>
            <select value={filters.section} onChange={(e) => setFilters({ ...filters, section: e.target.value })} className="px-3 py-2 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white text-sm">
              <option value="">Toutes les sections</option>
              <option value="VCN">VCN (Université)</option>
              <option value="HDR">HDR</option>
              <option value="SANTE">SANTÉ</option>
            </select>
            <select value={filters.annee_ramadan} onChange={(e) => setFilters({ ...filters, annee_ramadan: parseInt(e.target.value) })} className="px-3 py-2 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white text-sm">
              <option value={2025}>📅 2025</option>
              <option value={2024}>📅 2024</option>
              <option value={2023}>📅 2023</option>
            </select>
          </div>
          <div className="flex justify-center">
            <div className="inline-flex items-center space-x-2 bg-gray-100 rounded-lg p-1">
              <button onClick={() => setViewMode('cards')} className={`px-4 py-2 rounded-md text-xs font-semibold transition-all ${viewMode === 'cards' ? 'bg-purple-600 text-white shadow-md' : 'text-gray-600 hover:text-gray-900'}`}>📋 Cartes</button>
              <button onClick={() => setViewMode('grid')} className={`px-4 py-2 rounded-md text-xs font-semibold transition-all ${viewMode === 'grid' ? 'bg-purple-600 text-white shadow-md' : 'text-gray-600 hover:text-gray-900'}`}>⚏ Grille</button>
            </div>
          </div>
        </div>
      </div>

      {/* Liste des Exposés Ramadan */}
      <section className="relative bg-gradient-to-br from-green-50/30 via-white to-green-50/50 py-6 sm:py-8 md:py-10 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 right-1/4 w-72 h-72 bg-green-200/15 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-1/4 w-80 h-80 bg-green-100/20 rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 lg:gap-6">
            <div className="hidden lg:block lg:col-span-1">
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-green-100/50 sticky top-4">
                <h3 className="text-lg font-bold text-gray-900 mb-4">🔍 Filtres</h3>
                <div className="space-y-4">
                  <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="w-full px-4 py-2 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white text-sm">
                    <option value="">Tous les statuts</option>
                    <option value="a_venir">⏳ À venir</option>
                    <option value="en_cours">🟢 En cours</option>
                    <option value="termine">✅ Terminé</option>
                    <option value="passe">📜 Passé</option>
                  </select>
                  <select value={filters.section} onChange={(e) => setFilters({ ...filters, section: e.target.value })} className="w-full px-4 py-2 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white text-sm">
                    <option value="">Toutes les sections</option>
                    <option value="VCN">VCN (Université)</option>
                    <option value="HDR">HDR</option>
                    <option value="SANTE">SANTÉ</option>
                  </select>
                  <select value={filters.annee_ramadan} onChange={(e) => setFilters({ ...filters, annee_ramadan: parseInt(e.target.value) })} className="w-full px-4 py-2 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white text-sm">
                    <option value={2025}>📅 2025</option>
                    <option value={2024}>📅 2024</option>
                    <option value={2023}>📅 2023</option>
                  </select>
                </div>
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex flex-col space-y-2">
                    <button onClick={() => setViewMode('cards')} className={`w-full px-4 py-2 rounded-lg text-sm font-semibold transition-all ${viewMode === 'cards' ? 'bg-green-600 text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>📋 Cartes</button>
                    <button onClick={() => setViewMode('grid')} className={`w-full px-4 py-2 rounded-lg text-sm font-semibold transition-all ${viewMode === 'grid' ? 'bg-green-600 text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>⚏ Grille</button>
                  </div>
                </div>
              </div>
            </div>
            <div className="lg:col-span-3">
              {loading ? (
                <div className="flex justify-center items-center py-32">
                  <div className="relative"><div className="animate-spin rounded-full h-20 w-20 border-4 border-green-200 border-t-green-700"></div></div>
                </div>
              ) : exposes.length > 0 ? (
                viewMode === 'cards' ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {exposes.map((expose, index) => (
                      <Link key={expose.id} to={`/ramadan/${expose.url_slug || expose.id}`} className="group animate-slide-up" style={{ animationDelay: `${index * 0.1}s` }}>
                        <div className="bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 overflow-hidden border border-green-100/50 h-full flex flex-col">
                          <div className="relative h-48 bg-gradient-to-br from-green-600 via-green-700 to-green-800 overflow-hidden">
                            {expose.photo_exposant ? (
                              <img src={expose.photo_exposant} alt={expose.prenom_exposant} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                            ) : (
                              <div className="w-full h-full flex flex-col items-center justify-center text-white"><span className="text-7xl mb-4 opacity-80">🌙</span>{expose.jour_ramadan && (<span className="text-2xl font-bold">Jour {expose.jour_ramadan}</span>)}</div>
                            )}
                            {expose.jour_ramadan && (<div className="absolute top-4 right-4"><span className="px-4 py-2 rounded-full text-sm font-bold bg-white/20 backdrop-blur-md text-white border border-white/30">Jour {expose.jour_ramadan}</span></div>)}
                          </div>
                          <div className="p-6 flex-1 flex flex-col">
                            <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-purple-700 transition-colors line-clamp-2">{expose.theme}</h3>
                            <div className="space-y-2 mb-4 flex-1">
                              <p className="text-gray-700 font-medium text-lg">👤 {expose.prenom_exposant} {expose.nom_exposant}</p>
                              <p className="text-gray-600 text-sm">🎓 {expose.classe}</p>
                              <div className="flex items-center space-x-4 text-sm text-gray-500"><span>📅 {new Date(expose.date_expose).toLocaleDateString('fr-FR')}</span></div>
                            </div>
                            <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                              <div className="flex items-center space-x-4 text-sm text-gray-500"><span className="flex items-center space-x-1"><span>👁️</span><span>{expose.nombre_vues || 0}</span></span><span className="flex items-center space-x-1"><span>❤️</span><span>{expose.nombre_likes || 0}</span></span></div>
                              <span className="text-purple-700 font-semibold text-sm group-hover:translate-x-2 transition-transform inline-flex items-center space-x-1"><span>Voir détails</span><span>→</span></span>
                            </div>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl border border-purple-100/50 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gradient-to-r from-purple-600 to-purple-700 text-white">
                          <tr>
                            <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wider">Jour</th>
                            <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wider">Thème</th>
                            <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wider">Exposant</th>
                            <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wider">Classe</th>
                            <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wider">Date</th>
                            <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wider">Statut</th>
                            <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wider">Stats</th>
                            <th className="px-6 py-4 text-center text-sm font-bold uppercase tracking-wider">Action</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {exposes.map((expose, index) => (
                            <tr key={expose.id} className="hover:bg-purple-50/50 transition-colors duration-200 animate-slide-up" style={{ animationDelay: `${index * 0.03}s` }}>
                              <td className="px-6 py-4 whitespace-nowrap">{expose.jour_ramadan ? (<div className="flex items-center space-x-2"><span className="text-sm font-bold text-green-700">Jour {expose.jour_ramadan}</span></div>) : (<span className="text-sm text-gray-400">-</span>)}</td>
                              <td className="px-6 py-4"><div className="text-sm font-bold text-gray-900 max-w-xs">{expose.theme}</div></td>
                              <td className="px-6 py-4 whitespace-nowrap"><div className="flex items-center space-x-3">{expose.photo_exposant && (<img src={expose.photo_exposant} alt={expose.prenom_exposant} className="w-10 h-10 rounded-full object-cover border-2 border-purple-200" />)}<div className="text-sm font-medium text-gray-900">{expose.prenom_exposant} {expose.nom_exposant}</div></div></td>
                              <td className="px-6 py-4 whitespace-nowrap"><span className="text-sm text-gray-600">{expose.classe}</span></td>
                              <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm text-gray-900">📅 {new Date(expose.date_expose).toLocaleDateString('fr-FR')}</div><div className="text-xs text-gray-500">Année {expose.annee_ramadan}</div></td>
                              <td className="px-6 py-4 whitespace-nowrap">{(() => { const statusLabel = getStatusLabel(expose.status_auto, expose.status); return (<span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusLabel.color}`}>{statusLabel.icon} {statusLabel.text}</span>); })()}</td>
                              <td className="px-6 py-4 whitespace-nowrap"><div className="flex items-center space-x-4 text-sm text-gray-600"><span className="flex items-center space-x-1"><span>👁️</span><span className="font-semibold">{expose.nombre_vues || 0}</span></span><span className="flex items-center space-x-1"><span>❤️</span><span className="font-semibold">{expose.nombre_likes || 0}</span></span><span className="flex items-center space-x-1"><span>💬</span><span className="font-semibold">{expose.nombre_commentaires || 0}</span></span></div></td>
                              <td className="px-6 py-4 whitespace-nowrap text-center"><Link to={`/ramadan/${expose.url_slug || expose.id}`} className="inline-flex items-center space-x-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-300 transform hover:scale-105"><span>Voir</span><span>→</span></Link></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )
              ) : (
                <div className="text-center py-32"><div className="inline-block bg-white/90 backdrop-blur-sm rounded-3xl p-12 shadow-xl border border-purple-100/50"><div className="text-8xl mb-6 animate-float">🌙</div><h3 className="text-3xl font-bold text-gray-900 mb-4">Aucun exposé Ramadan trouvé</h3><p className="text-gray-600 text-lg">Essayez de modifier vos filtres pour voir plus de résultats</p></div></div>
              )}

              {totalPages > 1 && !loading && (
                <div className="flex flex-col sm:flex-row justify-center items-center gap-3 sm:gap-2 sm:space-x-2 mt-12 px-4">
                  <button onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} disabled={currentPage === 1} className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-semibold text-sm sm:text-base transition-all duration-300 transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100">← Précédent</button>
                  <div className="flex items-center space-x-1 sm:space-x-2 overflow-x-auto pb-2 sm:pb-0 max-w-full">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum: number;
                      if (totalPages <= 5) pageNum = i + 1; else if (currentPage <= 3) pageNum = i + 1; else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i; else pageNum = currentPage - 2 + i;
                      return (<button key={pageNum} onClick={() => setCurrentPage(pageNum)} className={`px-3 sm:px-4 py-2 rounded-xl font-semibold text-sm sm:text-base transition-all duration-300 whitespace-nowrap flex-shrink-0 ${currentPage === pageNum ? 'bg-purple-600 text-white shadow-lg scale-110' : 'bg-white text-gray-700 hover:bg-purple-50 border border-purple-200 hover:border-purple-400'}`}>{pageNum}</button>);
                    })}
                  </div>
                  <button onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} disabled={currentPage === totalPages} className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-semibold text-sm sm:text-base transition-all duration-300 transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100">Suivant →</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
